import React, { useEffect, useRef, useState } from "react";
import { AppNavLink } from "../lib/route-primitives";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Check, ChevronRight, Globe, LifeBuoy, LogOut, Package, UserCircle } from "lucide-react";
import { SUPPORTED_LANGUAGES } from "@wedding/shared";
import { useOrganizerAuth } from "../contexts/organizer-auth-context";
import { organizerSupportApi } from "../lib/api";
import { cn } from "../lib/utils";
import { paths } from "../lib/paths";

export function UserMenu({ refreshUnread }: { refreshUnread?: number } = {}) {
  const { t, i18n } = useTranslation();
  const { organizer, logout } = useOrganizerAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState<"main" | "language">("main");
  const [hasUnread, setHasUnread] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Fetch unread support count; re-fetch when refreshUnread changes
  useEffect(() => {
    let cancelled = false;
    organizerSupportApi
      .list()
      .then((res) => {
        if (cancelled) return;
        const tickets = (res.data ?? []) as Array<{
          organizer_unread: number;
          status: string;
        }>;
        setHasUnread(
          tickets.some(
            (tk) =>
              tk.organizer_unread > 0 &&
              (tk.status === "open" || tk.status === "in_review"),
          ),
        );
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshUnread]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    function handle(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
        setView("main");
      }
    }
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [isOpen]);

  // Close on Escape — if in language view, go back; otherwise close
  useEffect(() => {
    if (!isOpen) return;
    function handle(e: KeyboardEvent) {
      if (e.key === "Escape") {
        if (view === "language") setView("main");
        else setIsOpen(false);
      }
    }
    document.addEventListener("keydown", handle);
    return () => document.removeEventListener("keydown", handle);
  }, [isOpen, view]);

  const initial =
    (organizer?.displayName ?? organizer?.email ?? "?")[0]?.toUpperCase() ?? "?";
  const displayName =
    organizer?.displayName ?? organizer?.email?.split("@")[0] ?? "";
  const email = organizer?.email ?? "";
  const currentLang = SUPPORTED_LANGUAGES.find((l) => l.code === i18n.language) ?? SUPPORTED_LANGUAGES[0];

  return (
    <div ref={ref} className="relative">
      {/* Avatar button */}
      <button
        onClick={() => { setIsOpen((v) => !v); setView("main"); }}
        aria-label={t("nav.profile")}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-controls="user-menu-dropdown"
        className="relative h-8 w-8 shrink-0 rounded-full bg-primary/20 text-primary text-sm font-bold flex items-center justify-center hover:bg-primary/30 transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
      >
        {initial}
        {hasUnread && (
          <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-red-500 animate-pulse" />
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div
          id="user-menu-dropdown"
          role="menu"
          className="absolute right-0 top-full mt-2 min-w-56 rounded-2xl border border-primary/10 bg-background/95 backdrop-blur-xl shadow-lg z-50 overflow-hidden"
        >
          {view === "main" ? (
            <>
              {/* Name + email header */}
              <div className="px-4 py-3 border-b border-primary/10">
                <p className="text-sm font-semibold truncate">{displayName}</p>
                <p className="text-xs text-muted-foreground truncate">{email}</p>
              </div>

              {/* Nav items */}
              <div className="p-1.5 space-y-0.5">
                <AppNavLink
                  to={paths.profile}
                  onClick={() => setIsOpen(false)}
                  role="menuitem"
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors cursor-pointer",
                      isActive
                        ? "bg-primary/10 text-primary"
                        : "text-foreground hover:bg-primary/5",
                    )
                  }
                >
                  <UserCircle className="h-4 w-4 shrink-0" />
                  {t("nav.profile")}
                </AppNavLink>

                <AppNavLink
                  to={paths.packages}
                  onClick={() => setIsOpen(false)}
                  role="menuitem"
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors cursor-pointer",
                      isActive
                        ? "bg-primary/10 text-primary"
                        : "text-foreground hover:bg-primary/5",
                    )
                  }
                >
                  <Package className="h-4 w-4 shrink-0" />
                  <span>{t("nav.packages")}</span>
                </AppNavLink>

                <AppNavLink
                  to={paths.support}
                  onClick={() => setIsOpen(false)}
                  role="menuitem"
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors cursor-pointer",
                      isActive
                        ? "bg-primary/10 text-primary"
                        : "text-foreground hover:bg-primary/5",
                    )
                  }
                >
                  <div className="relative shrink-0">
                    <LifeBuoy className="h-4 w-4" />
                    {hasUnread && (
                      <span className="absolute -top-0.5 -right-0.5 h-1.5 w-1.5 rounded-full bg-red-500" />
                    )}
                  </div>
                  {t("support.title")}
                  {hasUnread && (
                    <span className="ml-auto h-2 w-2 rounded-full bg-red-500 animate-pulse" />
                  )}
                </AppNavLink>

                {/* Language — single row, Figma style */}
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => setView("language")}
                  className="flex w-full items-center gap-3 px-3 py-2.5 rounded-md text-sm text-foreground hover:bg-primary/5 transition-colors cursor-pointer"
                >
                  <Globe className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="flex-1 text-left">{currentLang.label}</span>
                  <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                </button>
              </div>

              {/* Logout */}
              <div className="border-t border-primary/10 p-1.5">
                <button
                  onClick={() => {
                    setIsOpen(false);
                    void logout();
                  }}
                  role="menuitem"
                  className="flex w-full items-center gap-3 px-3 py-2.5 rounded-md text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors cursor-pointer"
                >
                  <LogOut className="h-4 w-4 shrink-0" />
                  {t("common.logout")}
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Language sub-panel */}
              <div className="flex items-center gap-2 px-3 py-3 border-b border-primary/10">
                <button
                  type="button"
                  onClick={() => setView("main")}
                  className="p-1 rounded-md hover:bg-primary/5 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                  aria-label="Back"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>
                <span className="text-sm font-medium">{t("common.language")}</span>
              </div>
              <div className="p-1.5 space-y-0.5">
                {SUPPORTED_LANGUAGES.map((lang) => {
                  const active = lang.code === i18n.language;
                  return (
                    <button
                      key={lang.code}
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        void i18n.changeLanguage(lang.code);
                        setView("main");
                      }}
                      className={cn(
                        "flex w-full items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors cursor-pointer",
                        active
                          ? "bg-primary/10 text-primary font-medium"
                          : "text-foreground hover:bg-primary/5",
                      )}
                    >
                      <Check className={cn("h-3.5 w-3.5 shrink-0", active ? "opacity-100" : "opacity-0")} />
                      {lang.label}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
